import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CreateContentDialog } from '@/components/explore/create-content-dialog';
import { CreateTemplateDialog } from '@/components/explore/create-template-dialog';
import { NewFromTemplateDialog } from '@/components/explore/new-from-template-dialog';
import { CreateObservationCaptureDialog } from '@/components/explore/create-observation-capture-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ContentSortSelect, type SortDirection, type SortField } from '@/components/widget/content-sort-select';
import { TagSelect } from '@/components/widget/tag-select';
import { Delete } from '@/features/content/Delete';
import {
  useApplySavedFilter,
  useContentAction,
  useContentCreate,
  useContentExport,
  useContentList,
  useFilterCreate,
  useFilterList,
  useFilterUpdate,
  useNavigateWithContentFlush,
  useToggleRead,
  useUser
} from '@/hooks';
import { filterKeys } from '@/hooks/useFilterQueries';
import { contentKeys } from '@/hooks/useContentQueries';
import { CANONICAL_LABELS, getContentTypeIcon } from '@/lib/content-labels';
import { cn } from '@/lib/utils.ts';
import { useComposerStore } from '@/store/useComposerStore';
import { useSearchStore } from '@/store/searchStore';
import { useSelectedItemStore } from '@/store/selectedItemStore.ts';
import { toContentHref } from '@/utils/content-ref';
import { mapSortFieldToOrderBy } from '@/utils/content-sort-utils';
import {
  buildExploreContentListQuery,
} from '@/utils/search-utils';
import { Content, ContentType, Filter, FilterScope } from '@vivport/backend/types';
import { ownerScopeToInputScope } from '@/lib/owner-scope';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardPlus,
  Download,
  Loader2,
  Mail,
  MailOpen,
  Plus,
  Save,
  Trash2
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface ExploreActionsProps {
  cursor?: number;
  setCursor?: (cursor: number) => void;
  limit?: number;
  sortOptions?: { field: SortField; direction: SortDirection };
  setSortOptions?: (options: { field: SortField; direction: SortDirection }) => void;
  sortExplicit?: boolean;
}

function MenuTypeIcon({ contentType }: { contentType: ContentType }) {
  const Icon = getContentTypeIcon(contentType);
  return <Icon className="mr-2 h-4 w-4" />;
}

export function ExploreActions({ cursor, setCursor, limit, sortOptions, setSortOptions, sortExplicit }: ExploreActionsProps) {
  const queryClient = useQueryClient();
  const { data: memberContext } = useUser();
  const generateDocumentMutation = useContentAction();
  const createContentMutation = useContentCreate();
  const exportMutation = useContentExport();
  const toggleReadMutation = useToggleRead();
  const filterListQuery = useFilterList();
  const filterCreateMutation = useFilterCreate();
  const filterUpdateMutation = useFilterUpdate();
  const isGenerating = generateDocumentMutation.isPending;
  const isCreatingReport = createContentMutation.isPending;
  const { selectedItems, clearSelectedItems, selectAllItems } = useSelectedItemStore();
  const {
    isLayoutDisplay,
    query,
    activeSavedFilterId,
    activeSavedFilterDefinition,
    setActiveSavedFilter
  } = useSearchStore();
  const applySavedFilterView = useApplySavedFilter();
  const openCompose = useComposerStore((state) => state.openCompose);
  const navigate = useNavigateWithContentFlush();
  const location = useLocation();
  const contentRouteMatch = useMatch('/content/:contentRef');
  const [disabledGenerateDocumentDialogOpen, setDisabledGenerateDocumentDialogOpen] = useState(false);
  const [createDialogType, setCreateDialogType] = useState<'ANCHOR' | 'EVENT' | 'URL' | null>(null);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [newFromTemplateOpen, setNewFromTemplateOpen] = useState(false);
  const [captureDialogMode, setCaptureDialogMode] = useState<'recording' | 'photo' | 'video' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentItemId, setCurrentItemId] = useState<number | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveScope, setSaveScope] = useState<FilterScope>('PRIVATE');
  const [editingFilterId, setEditingFilterId] = useState<number | null>(null);
  const [saveDialogError, setSaveDialogError] = useState<string | null>(null);
  const [localCursor, setLocalCursor] = useState<number>(0);
  const [localSortOptions, setLocalSortOptions] = useState<{ field: SortField; direction: SortDirection }>({
    field: 'datetime',
    direction: 'desc',
  });
  const [localSortExplicit, setLocalSortExplicit] = useState(false);
  const [pendingBoundaryNavigation, setPendingBoundaryNavigation] = useState<'first' | 'last' | null>(null);

  const resolveContentRef = useCallback((id: number): string => {
    const cached = queryClient.getQueryData<{ code?: string }>(contentKeys.detail(id));
    return typeof cached?.code === 'string' && cached.code.length > 0 ? cached.code : String(id);
  }, [queryClient]);

  const effectiveCursor = cursor ?? localCursor;
  const effectiveSetCursor = setCursor ?? setLocalCursor;
  const effectiveLimit = limit ?? 20;
  const effectiveSortOptions = sortOptions ?? localSortOptions;
  const effectiveSortExplicit = sortExplicit ?? localSortExplicit;
  const effectiveSetSortOptions =
    setSortOptions ??
    ((options: { field: SortField; direction: SortDirection }) => {
      setLocalSortOptions(options);
      setLocalSortExplicit(true);
    });
  const savedDefinition = activeSavedFilterDefinition;
  const assembledContentListQuery = useMemo(() => {
    return buildExploreContentListQuery({
      query,
      tabFilters: memberContext?.tabFilters,
      savedDefinition,
      orderBy: mapSortFieldToOrderBy(effectiveSortOptions.field),
      orderDir: effectiveSortOptions.direction,
      sortExplicit: effectiveSortExplicit,
      cursor: effectiveCursor,
      limit: effectiveLimit,
    });
  }, [
    query,
    memberContext?.tabFilters,
    savedDefinition,
    effectiveSortOptions.field,
    effectiveSortOptions.direction,
    effectiveSortExplicit,
    effectiveCursor,
    effectiveLimit,
  ]);
  const activeContentTypes = assembledContentListQuery.state.contentTypes;
  const activeSections = assembledContentListQuery.state.sections;
  const activeProjection = assembledContentListQuery.state.projection;
  const resolvedSearch = assembledContentListQuery.state.search;

  // Fetch current page data
  const contentQuery = useContentList(assembledContentListQuery.request);

  const { data: contentData } = contentQuery;
  const contentIds = useMemo(() => contentData?.ids ?? [], [contentData?.ids]);
  const totalItems = contentData?.totalItems || 0;
  const nextCursor = contentData?.nextCursor;
  const savedFilterIds = filterListQuery.data?.ids || [];
  const savedFilters = useMemo(() => {
    return savedFilterIds
      .map((id) => queryClient.getQueryData(filterKeys.detail(id)) as Filter | undefined)
      .filter((item): item is Filter => item !== undefined);
  }, [savedFilterIds, queryClient, filterListQuery.dataUpdatedAt]);

  // Save what is actually being listed: the assembled request already resolved an
  // ordering the projection supports, and the filter schema rejects a mismatch.
  const currentDefinition = {
    projection: activeProjection ?? 'CONTENT',
    contentTypes: activeContentTypes,
    sections: activeSections,
    obligations: savedDefinition?.obligations,
    createdByMemberIds: savedDefinition?.createdByMemberIds,
    tagIds: savedDefinition?.tagIds,
    search: resolvedSearch,
    dateFrom: savedDefinition?.dateFrom,
    dateTo: savedDefinition?.dateTo,
    isRead: savedDefinition?.isRead,
    isStarred: savedDefinition?.isStarred,
    isFollowing: savedDefinition?.isFollowing,
    orderBy: assembledContentListQuery.request.orderBy,
    orderDir: assembledContentListQuery.request.orderDir,
  };

  const openSaveDialog = (filter?: Filter): void => {
    setSaveDialogError(null);
    filterCreateMutation.reset();
    filterUpdateMutation.reset();
    if (filter) {
      setEditingFilterId(filter.id);
      setSaveName(filter.name);
      setSaveDescription(filter.description ?? '');
      setSaveScope(ownerScopeToInputScope(filter.ownerScope));
    } else {
      setEditingFilterId(null);
      setSaveName('');
      setSaveDescription('');
      const activeFilter = savedFilters.find(item => item.id === activeSavedFilterId);
      const activeScope = activeFilter ? ownerScopeToInputScope(activeFilter.ownerScope) : 'PRIVATE';
      setSaveScope(activeScope);
    }
    setSaveDialogOpen(true);
  };

  const handleSaveDialogOpenChange = (open: boolean): void => {
    setSaveDialogOpen(open);
    if (!open) {
      setSaveDialogError(null);
      filterCreateMutation.reset();
      filterUpdateMutation.reset();
    }
  };

  const getSaveDialogErrorMessage = (error: unknown): string => {
    const axiosError = error as AxiosError<{ code?: string; message?: string }>;
    const status = axiosError.response?.status;
    const code = axiosError.response?.data?.code;

    if (status === 409 || code === 'FILTER_NAME_CONFLICT') {
      return 'A saved filter with this name already exists. Please choose a different name.';
    }

    if (status === 403 || code === 'INSUFFICIENT_PERMISSIONS') {
      return editingFilterId
        ? 'You do not have permission to update this saved filter.'
        : 'You do not have permission to save this filter with the selected scope.';
    }

    return axiosError.response?.data?.message ?? 'Unable to save filter. Please try again.';
  };

  const applySavedFilter = (filter: Filter): void => {
    applySavedFilterView(filter, navigate);
  };

  const saveCurrentFilter = (): void => {
    setSaveDialogError(null);
    const payload = {
      name: saveName.trim(),
      description: saveDescription.trim() || undefined,
      scope: saveScope,
      definition: currentDefinition
    };

    if (editingFilterId) {
      filterUpdateMutation.mutate({
        id: editingFilterId,
        data: {
          name: payload.name,
          description: payload.description,
          definition: payload.definition
        }
      }, {
        onSuccess: (updated) => {
          setActiveSavedFilter(updated.id, updated.definition, updated.name);
          setSaveDialogError(null);
          handleSaveDialogOpenChange(false);
        },
        onError: (error) => {
          setSaveDialogError(getSaveDialogErrorMessage(error));
        }
      });
      return;
    }

    filterCreateMutation.mutate(payload, {
      onSuccess: (created) => {
        applySavedFilter(created);
        setSaveDialogError(null);
        handleSaveDialogOpenChange(false);
      },
      onError: (error) => {
        setSaveDialogError(getSaveDialogErrorMessage(error));
      }
    });
  };

  // Check if we're in display mode (detail page) - pagination/sorting not available
  const isDisplayMode = isLayoutDisplay();

  // Calculate pagination info
  // Note: cursor will be reset by useEffect if out of bounds, so we can use it directly here
  const safeCursor = effectiveCursor;
  const startResult = totalItems === 0 ? 0 : safeCursor + 1;
  const endResult = totalItems === 0 ? 0 : Math.min(safeCursor + contentIds.length, totalItems);
  
  // Ensure startResult <= endResult (defensive check)
  const safeStartResult = startResult > endResult ? Math.max(1, endResult - contentIds.length + 1) : startResult;
  const safeEndResult = startResult > endResult ? endResult : endResult;

  // Get current item index within current page
  const currentItemIndex = currentItemId ? contentIds.indexOf(currentItemId) : -1;
  const previousItemId = currentItemIndex > 0 ? contentIds[currentItemIndex - 1] : null;
  const nextItemId = currentItemIndex >= 0 && currentItemIndex < contentIds.length - 1 ? contentIds[currentItemIndex + 1] : null;

  // Update current item ID from URL
  const pathContentRef = contentRouteMatch?.params.contentRef?.trim() ?? '';
  const pathContentId = useMemo(() => {
    if (!pathContentRef || contentIds.length === 0) {
      return null;
    }

    for (const id of contentIds) {
      const resolvedRef = resolveContentRef(id);
      if (resolvedRef === pathContentRef || String(id) === pathContentRef) {
        return id;
      }
    }

    return null;
  }, [contentIds, pathContentRef, resolveContentRef]);

  // Set current item when navigating to a content page
  useEffect(() => {
    if (pathContentId && pathContentId !== currentItemId) {
      setCurrentItemId(pathContentId);
    }
  }, [pathContentId, currentItemId]);

  useEffect(() => {
    setDeleteDialogOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (selectedItems.length === 0) {
      setDeleteDialogOpen(false);
    }
  }, [selectedItems.length]);

  // Reset cursor if it's out of bounds (e.g., after switching tabs with fewer items)
  useEffect(() => {
    if (totalItems > 0 && effectiveCursor >= totalItems) {
      effectiveSetCursor(0);
    }
  }, [effectiveCursor, totalItems, effectiveSetCursor]);

  useEffect(() => {
    if (cursor !== undefined) {
      return;
    }
    setLocalCursor(0);
  }, [query, activeSavedFilterId, effectiveSortOptions.field, effectiveSortOptions.direction, cursor]);

  useEffect(() => {
    if (!pendingBoundaryNavigation || contentIds.length === 0) {
      return;
    }
    const targetId = pendingBoundaryNavigation === 'first' ? contentIds[0] : contentIds[contentIds.length - 1];
    if (!targetId) {
      return;
    }
    setCurrentItemId(targetId);
    navigate(toContentHref(resolveContentRef(targetId)));
    setPendingBoundaryNavigation(null);
  }, [pendingBoundaryNavigation, contentIds, navigate, resolveContentRef]);

  // Convert current page content IDs to selectable IDs
  const selectableContentIds = contentIds;

  // Determine checkbox state
  const allItemsSelected = selectableContentIds.length > 0 && selectedItems.length === selectableContentIds.length;
  const someItemsSelected = selectedItems.length > 0 && selectedItems.length < selectableContentIds.length;

  const handleSelectAllChange = () => {
    if (allItemsSelected || someItemsSelected) {
      // If all or some items are selected, deselect all
      clearSelectedItems();
    } else {
      // If no items are selected, select all
      selectAllItems(selectableContentIds);
    }
  };

  // Determine the checked state for the checkbox
  const getCheckedState = () => {
    if (allItemsSelected) return true;
    if (someItemsSelected) return 'indeterminate' as const;
    return false;
  };


  const handleDownloadSelection = async () => {
    if (selectedItems.length === 0 || exportMutation.isPending) {
      return;
    }
    try {
      await exportMutation.mutateAsync(selectedItems);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleBulkRead = (isRead: boolean) => {
    if (selectedItems.length === 0) {
      return;
    }
    selectedItems.forEach((contentId) => {
      toggleReadMutation.mutate({ contentId, isRead });
    });
  };

  const nextActionIsRead =
    selectedItems.length === 0 ||
    !selectedItems.every(
      (id) => queryClient.getQueryData<Content>(contentKeys.detail(id))?.tracking?.isRead === true
    );

  const handlePageChange = (direction: 'next' | 'prev') => {
    if (direction === 'next') {
      if (nextCursor !== null && nextCursor !== undefined) {
        effectiveSetCursor(nextCursor);
        setCurrentItemId(null); // Reset current item when changing pages
      }
    } else {
      // Go back by exactly one page (limit items)
      const prevCursor = Math.max(0, effectiveCursor - effectiveLimit);
      effectiveSetCursor(prevCursor);
      setCurrentItemId(null); // Reset current item when changing pages
    }
  };
  
  const renderPaginationListControls = () => {
    if (isDisplayMode) return null;
    
    return (
      <div className="flex items-center gap-4">
        <ContentSortSelect
          value={effectiveSortOptions}
          onValueChange={effectiveSetSortOptions}
          disabled={totalItems === 0}
        />
        <div className="text-sm text-muted-foreground">
          <span className="hidden md:inline">Results </span>
          {`${startResult}-${endResult} of ${totalItems}`}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="pagination-left"
                className="h-8 w-8"
                onClick={() => handlePageChange('prev')}
                disabled={safeCursor === 0 || totalItems === 0}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Previous Page</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="pagination-right"
                className="h-8 w-8"
                onClick={() => handlePageChange('next')}
                disabled={nextCursor === null || totalItems === 0}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Next Page</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  };

  const handleGenerateReportFromSelection = () => {
    if (isGenerating) return;
    if (selectedItems.length === 0) {
      setDisabledGenerateDocumentDialogOpen(true);
      return;
    }

    const EventIds = selectedItems;
    if (EventIds.length > 0) {
      generateDocumentMutation.mutate({
        id: EventIds[0],
        actionName: 'generateContent',
        params: {}
      }, {
        onSuccess: () => {
          clearSelectedItems();
        }
      });
    }
  };

  const handleCreateReport = async () => {
    if (isCreatingReport) {
      return;
    }
    const created = await createContentMutation.mutateAsync({
      contentType: ContentType.DOCUMENT,
            title: 'New document',
    });
    navigate(toContentHref(created.code));
  };

  const handleUpload = async (file: File) => {
    const created = await createContentMutation.mutateAsync({
      contentType: ContentType.FILE,
      file,
      title: file.name,
    });
    navigate(toContentHref(created.code));
  };

  const renderPaginationListControlsWithoutSort = () => {
    if (isDisplayMode) return null;
    
    return (
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          <span className="hidden md:inline">Results </span>
          {`${safeStartResult}-${safeEndResult} of ${totalItems}`}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="pagination-left"
                className="h-8 w-8"
                onClick={() => handlePageChange('prev')}
                disabled={safeCursor === 0 || totalItems === 0}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Previous Page</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="pagination-right"
                className="h-8 w-8"
                onClick={() => handlePageChange('next')}
                disabled={nextCursor === null || totalItems === 0}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Next Page</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderPaginationBrowseControls = () => {
    if (totalItems === 0) {
      return <p className="text-sm text-muted-foreground">No items to browse.</p>;
    }

    const currentItemPosition = currentItemId && currentItemIndex >= 0 
      ? safeCursor + currentItemIndex + 1 
      : 0;
  
    return (
      <div className="flex items-center gap-4">
        <ContentSortSelect
          value={effectiveSortOptions}
          onValueChange={effectiveSetSortOptions}
          disabled={totalItems === 0}
        />
        <div className="text-sm text-muted-foreground">
          {currentItemPosition > 0 ? `${currentItemPosition} of ${totalItems}` : `0 of ${totalItems}`}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="pagination-left"
                className="h-8 w-8"
                onClick={() => {
                  if (previousItemId) {
                    setCurrentItemId(previousItemId);
                    navigate(toContentHref(resolveContentRef(previousItemId)));
                  } else if (effectiveCursor > 0) {
                    // Need to fetch previous page
                    const prevCursor = Math.max(0, effectiveCursor - effectiveLimit);
                    setPendingBoundaryNavigation('last');
                    effectiveSetCursor(prevCursor);
                  }
                }}
                disabled={!previousItemId && safeCursor === 0}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Previous Item</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="pagination-right"
                className="h-8 w-8"
                onClick={() => {
                  if (nextItemId) {
                    setCurrentItemId(nextItemId);
                    navigate(toContentHref(resolveContentRef(nextItemId)));
                  } else if (nextCursor !== null && nextCursor !== undefined) {
                    // Need to fetch next page
                    setPendingBoundaryNavigation('first');
                    effectiveSetCursor(nextCursor);
                  }
                }}
                disabled={!nextItemId && nextCursor === null}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Next Item</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderPaginationBrowseControlsWithoutSort = () => {
    if (totalItems === 0) {
      return <p className="text-sm text-muted-foreground">No items to browse.</p>;
    }

    const currentItemPosition = currentItemId && currentItemIndex >= 0 
      ? safeCursor + currentItemIndex + 1 
      : 0;
  
    return (
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {currentItemPosition > 0 ? `${currentItemPosition} of ${totalItems}` : `0 of ${totalItems}`}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  if (previousItemId) {
                    setCurrentItemId(previousItemId);
                    navigate(toContentHref(resolveContentRef(previousItemId)));
                  } else if (effectiveCursor > 0) {
                    const prevCursor = Math.max(0, effectiveCursor - effectiveLimit);
                    setPendingBoundaryNavigation('last');
                    effectiveSetCursor(prevCursor);
                  }
                }}
                disabled={!previousItemId && safeCursor === 0}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Previous Item</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  if (nextItemId) {
                    setCurrentItemId(nextItemId);
                    navigate(toContentHref(resolveContentRef(nextItemId)));
                  } else if (nextCursor !== null && nextCursor !== undefined) {
                    setPendingBoundaryNavigation('first');
                    effectiveSetCursor(nextCursor);
                  }
                }}
                disabled={!nextItemId && nextCursor === null}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Next Item</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  };

  
  return (
    <>
      <div className="bg-muted px-4 pt-4 pb-2">
        <div className="flex flex-col gap-2">
          <TooltipProvider>
            {/* First row: Checkbox and actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Checkbox
                  className="mr-2"
                  checked={getCheckedState()}
                  onCheckedChange={handleSelectAllChange}
                />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openSaveDialog()}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Save Current Filter</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'h-8 w-8',
                        selectedItems.length === 0 ? 'opacity-50' : ''
                      )}
                      aria-label={nextActionIsRead ? 'Mark selected as read' : 'Mark selected as unread'}
                      onClick={() => handleBulkRead(nextActionIsRead)}
                    >
                      {nextActionIsRead ? (
                        <MailOpen className="h-4 w-4" />
                      ) : (
                        <Mail className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{nextActionIsRead ? 'Mark as read' : 'Mark as unread'}</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <TagSelect />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Manage Tags</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'h-8 w-8',
                        selectedItems.length === 0 ? 'opacity-50' : ''
                      )}
                      aria-label="Download selected"
                      onClick={handleDownloadSelection}
                    >
                      {exportMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download selected</p>
                  </TooltipContent>
                </Tooltip>

                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Add content"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add content</p>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="start" className="w-60">
                    <DropdownMenuItem onSelect={() => setCreateDialogType('ANCHOR')}>
                      <MenuTypeIcon contentType={ContentType.ANCHOR} />
                      New {CANONICAL_LABELS.anchor}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => { void handleCreateReport(); }}>
                      {isCreatingReport ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <MenuTypeIcon contentType={ContentType.DOCUMENT} />
                      )}
                      New {CANONICAL_LABELS.document}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCreateDialogType('EVENT')}>
                      <MenuTypeIcon contentType={ContentType.EVENT} />
                      New {CANONICAL_LABELS.event}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => openCompose()}>
                      <MenuTypeIcon contentType={ContentType.MESSAGE} />
                      New {CANONICAL_LABELS.message}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCaptureDialogMode('photo')}>
                      <MenuTypeIcon contentType={ContentType.PHOTO} />
                      New {CANONICAL_LABELS.photo}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCaptureDialogMode('recording')}>
                      <MenuTypeIcon contentType={ContentType.RECORDING} />
                      New {CANONICAL_LABELS.recording}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCaptureDialogMode('video')}>
                      <MenuTypeIcon contentType={ContentType.VIDEO} />
                      New {CANONICAL_LABELS.video}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => { setTimeout(() => fileInputRef.current?.click(), 0); }}>
                      <MenuTypeIcon contentType={ContentType.FILE} />
                      New {CANONICAL_LABELS.file}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCreateDialogType('URL')}>
                      <MenuTypeIcon contentType={ContentType.URL} />
                      New {CANONICAL_LABELS.url}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCreateTemplateOpen(true)}>
                      <MenuTypeIcon contentType={ContentType.TEMPLATE} />
                      New {CANONICAL_LABELS.template}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setNewFromTemplateOpen(true)}>
                      <MenuTypeIcon contentType={ContentType.TEMPLATE} />
                      New from template
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={handleGenerateReportFromSelection}>
                      {isGenerating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ClipboardPlus className="mr-2 h-4 w-4" />
                      )}
                      Generate {CANONICAL_LABELS.document}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'h-8 w-8',
                        selectedItems.length === 0 ? 'opacity-50' : ''
                      )}
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          return;
                        }
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete Selected Items</p>
                  </TooltipContent>
                </Tooltip>

                {selectedItems.length > 0 && (
                  <p className={'text-muted-foreground text-sm italic'}>
                    {selectedItems.length}
                    <span className="hidden sm:inline"> selected</span>
                  </p>
                )}
              </div>
              
              {/* Show pagination on larger screens only */}
              <div className="hidden md:block">
                {isLayoutDisplay() ? renderPaginationBrowseControls() : renderPaginationListControls()}
              </div>
            </div>
            
            {/* Second row: Sort and pagination on smaller screens */}
            <div className="flex items-center justify-between md:hidden">
              <div className="flex items-center">
                <ContentSortSelect
                  value={effectiveSortOptions}
                  onValueChange={effectiveSetSortOptions}
                  disabled={totalItems === 0}
                />
              </div>
              <div className="flex items-center">
                {isLayoutDisplay() ? renderPaginationBrowseControlsWithoutSort() : renderPaginationListControlsWithoutSort()}
              </div>
            </div>

          </TooltipProvider>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleUpload(f); e.target.value = ''; }}
      />
      {createDialogType !== null && (
        <CreateContentDialog
          open
          initialType={createDialogType}
          onOpenChange={(next) => { if (!next) setCreateDialogType(null); }}
        />
      )}
      <CreateTemplateDialog open={createTemplateOpen} onOpenChange={setCreateTemplateOpen} />
      <NewFromTemplateDialog open={newFromTemplateOpen} onOpenChange={setNewFromTemplateOpen} />

      {captureDialogMode !== null && (
        <CreateObservationCaptureDialog
          open
          defaultMode={captureDialogMode}
          onOpenChange={(next) => { if (!next) setCaptureDialogMode(null); }}
        />
      )}
      <Dialog open={disabledGenerateDocumentDialogOpen} onOpenChange={setDisabledGenerateDocumentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot generate {CANONICAL_LABELS.document}</DialogTitle>
            <DialogDescription>
              <ul className="list-disc list-inside text-left">
                <li>
                  Please select at least one <strong>Event</strong> or <strong>Document</strong> to generate an document.
                </li>
              </ul>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={saveDialogOpen} onOpenChange={handleSaveDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFilterId ? 'Edit Saved Filter' : 'Save Current Filter'}</DialogTitle>
            <DialogDescription>
              Save the current Explore filter state for quick reuse.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <Input
              placeholder="Filter name"
              value={saveName}
              onChange={(event) => {
                setSaveName(event.target.value);
                if (saveDialogError) {
                  setSaveDialogError(null);
                }
              }}
            />
            <Input
              placeholder="Description (optional)"
              value={saveDescription}
              onChange={(event) => {
                setSaveDescription(event.target.value);
                if (saveDialogError) {
                  setSaveDialogError(null);
                }
              }}
            />
            {!editingFilterId && (
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={saveScope}
                onChange={(event) => {
                  setSaveScope(event.target.value as FilterScope);
                  if (saveDialogError) {
                    setSaveDialogError(null);
                  }
                }}
              >
                <option value="PRIVATE">Private</option>
                <option value="HUB" disabled={!memberContext?.isHubAdmin}>Hub (admins only)</option>
              </select>
            )}
            {saveDialogError && (
              <p className="text-sm text-destructive" role="alert">
                {saveDialogError}
              </p>
            )}
          </DialogBody>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleSaveDialogOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={!saveName.trim() || filterCreateMutation.isPending || filterUpdateMutation.isPending}
              onClick={saveCurrentFilter}
            >
              {editingFilterId ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Delete
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedItems={selectedItems}
        onDeleted={() => clearSelectedItems()}
        onPartiallyDeleted={() => {
          // Deliberately does not clear: the items that did not go stay selected so the
          // member can act on them. The lists have already refreshed through the commit
          // mutation's own cache invalidation, so there is nothing further to do here.
        }}
      />
    </>
  );
}


